import { WithDefaultProps } from 'browser/components/atomic-elements/higher-order-components/with-default-props'
import { RecipientsField } from 'browser/components/atomic-elements/molecules/fields/recipients-field'

import '../../styles/_control.scss'
import { JSONSelectFactory } from 'browser/components/json-elements/higher-order-components/json-select-factory'
import { ISelectProps } from 'browser/components/atomic-elements/atoms/select/select/interface'
import { getDeferredUser } from 'shared-libs/helpers/utils'
import { shareKey } from 'shared-libs/helpers/recipients'
import { Entity } from 'shared-libs/models/entity'
import type { CSSObject } from '@emotion/serialize'
import _ from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'

export interface IMobileRecipientsFieldProps extends ISelectProps {
  entity?: Entity
  lockExistingShares?: boolean
}

const shareToRecipient = (share: Entity | any): string | undefined => shareKey(share) || share?.uniqueId

const MobileRecipientsFieldImpl: React.FC<IMobileRecipientsFieldProps> = (props: IMobileRecipientsFieldProps) => {
  const { onChange, entity, lockExistingShares = true } = props
  const [value, setValue] = useState<string[]>([])
  const [shares, setShares] = useState<Entity[]>([])
  const [initialRecipients, setInitialRecipients] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setIsLoading(true)
    entity.fetchShares()
      .then((res) => {
        const shareEntities = res?.children || []
        const shareValues = _.compact(_.map(shareEntities, shareToRecipient))
        setShares(shareEntities)
        setValue(shareValues)
        setInitialRecipients((prev) => (_.isEmpty(prev) ? shareValues : prev))
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }, [])

  const handleOnChange = useCallback(async (rawNextValue: string[] = [], options) => {
    setIsLoading(true)
    let nextValue = _.uniqWith(rawNextValue, _.isEqual)
    if (!entity) {
      setValue(nextValue)
      onChange?.(nextValue, options)
      setIsLoading(false)
      return
    }

    const addedRecipients = _.difference(nextValue, value)
    for (const recipient of addedRecipients) {
      if (shares.some((share) => shareToRecipient(share) == recipient)) {
        continue
      }
      const contact = getDeferredUser(recipient)
      try {
        const share = await entity.addShare(contact, null, true)
        setShares((prevShares) => [...prevShares, share])
        const formattedRecipient = shareToRecipient(share)
        if (formattedRecipient) {
          nextValue = nextValue.map((existingRecipient) => (
            existingRecipient === recipient ? formattedRecipient : existingRecipient
          ))
        }
      } catch (e) {
        console.error('Error adding share', e)
      }
    }

    nextValue = _.uniqWith(nextValue, _.isEqual) // normalization can re-introduce duplicates

    const removedRecipients = _.difference(value, nextValue)
    for (const recipient of removedRecipients) {
      const shareEntity = shares.find((share) => shareToRecipient(share) == recipient)
      if (!shareEntity) { continue }
      try {
        await shareEntity.delete()
      } catch (e) {
        console.error('Error removing share', e)
        continue
      }
      setShares((prevShares) => prevShares.filter((share) => share.uniqueId !== shareEntity.uniqueId))
    }

    setValue(nextValue)
    onChange?.(nextValue, options)
    setIsLoading(false)
  }, [entity, onChange, shares, value])

  const lockedRecipients = lockExistingShares ? _.uniq(initialRecipients) : []

  return (
    <RecipientsField
      {...props}
      onChange={handleOnChange}
      value={[...value]}
      lockedValues={lockedRecipients}
      isLoading={isLoading}
      styles={{
        valueContainer: (base) => ({
          ...base,
          height: '44pt',
          overflowY: 'auto',
        }) as CSSObject,
        control: (base) => ({
          ...base,
          height: '44pt',
        }) as CSSObject,
      }}
    />
  )
}

const UIRecipientsField = JSONSelectFactory(MobileRecipientsFieldImpl)

export const MobileRecipientsField = WithDefaultProps(UIRecipientsField, {
  inputClassName: 'mobile-control',
})
